
import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Twitter, Linkedin, Youtube, Globe } from 'lucide-react';
import { SocialLink } from '@/types/course';

interface InstructorProfileProps {
  creator: {
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
    bio?: string | null;
  };
  additionalInfo?: {
    creatorBio?: string;
    socialLinks?: SocialLink[];
  } | null;
}

export function InstructorProfile({ creator, additionalInfo }: InstructorProfileProps) {
  const getSocialIcon = (platform: string) => {
    const iconProps = { className: "h-4 w-4" };
    switch (platform.toLowerCase()) {
      case 'twitter': return <Twitter {...iconProps} />;
      case 'linkedin': return <Linkedin {...iconProps} />;
      case 'youtube': return <Youtube {...iconProps} />;
      default: return <Globe {...iconProps} />;
    }
  };

  const getSocialUrl = (link: SocialLink) => {
    return link.url || (link.username ? `https://${link.platform}.com/${link.username}` : '#');
  };

  const getInitials = () => {
    if (!creator.full_name) return "IN";
    return creator.full_name
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <section id="instructor" className="py-6 border-t">
      <h2 className="text-2xl font-bold mb-6">About the Instructor</h2>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center sm:items-start">
              <Avatar className="h-24 w-24 mb-4">
                <AvatarImage src={creator.avatar_url || undefined} />
                <AvatarFallback>{getInitials()}</AvatarFallback>
              </Avatar>
              
              <h3 className="text-xl font-semibold mb-2">
                {creator.full_name || creator.username}
              </h3>
              
              {additionalInfo?.socialLinks && additionalInfo.socialLinks.length > 0 && (
                <div className="flex gap-2 mt-4">
                  {additionalInfo.socialLinks.map((link, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      asChild
                    >
                      <a 
                        href={getSocialUrl(link)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label={`Visit ${link.platform}`}
                      >
                        {getSocialIcon(link.platform)}
                      </a>
                    </Button>
                  ))}
                </div>
              )}
              
              {creator.username && (
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  asChild
                >
                  <Link to={`/${creator.username}`}>
                    View All Courses
                  </Link>
                </Button>
              )}
            </div>
            
            <div className="flex-1">
              <div className="prose max-w-none">
                <p className="text-muted-foreground">
                  {additionalInfo?.creatorBio || creator.bio || 
                   "This instructor hasn't added a bio yet."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
